import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rfiApi } from '../services/api'
import toast from 'react-hot-toast'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface RigaListino {
  id: number
  tariffa: string
  descrizione: string
  unita_misura?: string
  prezzo: number
}

interface PaginatedListino {
  items: RigaListino[]
  total: number
  page: number
  per_page: number
  pages: number
}

const PER_PAGE = 50

export default function RfiListino() {
  const qc = useQueryClient()
  const [listinoNome, setListinoNome] = useState<string>('')
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const t = setTimeout(() => { setSearchDebounced(search); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [search])

  const { data: nomi = [] } = useQuery({
    queryKey: ['rfi-listino-nomi'],
    queryFn: () => rfiApi.nomiListino().then(r => r.data as string[]),
  })

  useEffect(() => {
    if (!listinoNome && nomi.length > 0) setListinoNome(nomi[0])
  }, [nomi, listinoNome])

  const { data, isLoading } = useQuery({
    queryKey: ['rfi-listino', listinoNome, searchDebounced, page],
    queryFn: () => rfiApi
      .listaListino({ listino_nome: listinoNome, search: searchDebounced || undefined, page, per_page: PER_PAGE })
      .then(r => r.data as PaginatedListino),
    enabled: !!listinoNome,
  })

  const updateMut = useMutation({
    mutationFn: ({ id, campo, valore }: { id: number; campo: string; valore: string | number }) =>
      rfiApi.aggiornaRigaListino(id, { [campo]: valore }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rfi-listino', listinoNome, searchDebounced, page] }),
    onError: () => toast.error('Salvataggio riga non riuscito'),
  })

  const salvaCampo = (id: number, campo: string, valoreOriginale: string | number, nuovoValore: string) => {
    if (campo === 'prezzo') {
      const num = parseFloat(nuovoValore.replace(',', '.'))
      if (Number.isNaN(num)) { toast.error('Prezzo non valido'); return }
      if (num === valoreOriginale) return
      updateMut.mutate({ id, campo, valore: num })
    } else {
      if (nuovoValore === valoreOriginale) return
      updateMut.mutate({ id, campo, valore: nuovoValore })
    }
  }

  const items = data?.items ?? []
  const pages = data?.pages ?? 1

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-lg font-semibold">Listino Prezzi RFI</h1>

      <div className="card p-4 flex flex-wrap gap-2 items-center">
        <select className="select" value={listinoNome} onChange={e => { setListinoNome(e.target.value); setPage(1) }}>
          {nomi.length === 0 && <option value="">Nessun listino importato</option>}
          {nomi.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Cerca per tariffa o descrizione..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="text-xs text-slate-500">{data ? `${data.total} voci` : ''}</span>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase">
              <th className="p-2">Tariffa</th>
              <th className="p-2">Descrizione</th>
              <th className="p-2">Unità</th>
              <th className="p-2">Prezzo</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={4} className="p-4 text-center text-slate-500">Caricamento...</td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={4} className="p-4 text-center text-slate-500">Nessuna voce trovata</td></tr>
            )}
            {items.map(r => (
              <tr key={r.id} className="border-t">
                <td className="p-1">
                  <input
                    className="input w-full"
                    defaultValue={r.tariffa}
                    onBlur={e => salvaCampo(r.id, 'tariffa', r.tariffa, e.target.value)}
                  />
                </td>
                <td className="p-1">
                  <input
                    className="input w-full"
                    defaultValue={r.descrizione}
                    onBlur={e => salvaCampo(r.id, 'descrizione', r.descrizione, e.target.value)}
                  />
                </td>
                <td className="p-1">
                  <input
                    className="input w-20"
                    defaultValue={r.unita_misura ?? ''}
                    onBlur={e => salvaCampo(r.id, 'unita_misura', r.unita_misura ?? '', e.target.value)}
                  />
                </td>
                <td className="p-1">
                  <input
                    className="input w-24"
                    defaultValue={r.prezzo}
                    onBlur={e => salvaCampo(r.id, 'prezzo', r.prezzo, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-slate-600">Pagina {page} di {pages}</span>
          <button className="btn-ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  )
}
